'use client'

import { useSyncExternalStore } from 'react'
import { analytics, trackEvent, type EventProperties } from '@/lib/analytics'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'
import { appInstallAvailableNow, useAppInstallAvailable } from '@/hooks/use-app-install-available'
import {
  isAppPromoLive,
  AppStoreBadgeLink,
  useAppCtaCopyVariant,
} from '@/components/ui/app-download-cta'
import { AppStoreQrCode } from '@/components/ui/app-store-qr'

/**
 * The card occupies the v2 rail only while Mediavine places no sidebar unit
 * there. Their ad-ops enable the placement by setting `sidebar_atf_selector`
 * server-side — no deploy of ours — so the card has to notice the ad's
 * arrival at runtime and yield, rather than wait for a removal PR (see
 * obsidian/features/mediavine-journey-ads.md). `.adunit` is the wrapper's
 * placeholder class, present before the slot even fills; the iframe check is
 * the failsafe if that class ever changes. Fails open by design: if detection
 * misses, the card shows alongside the ad — a visible layout problem, not a
 * silent one. The v1 rail has no #wgis-blog-rail id (Journey refuses sticky
 * sidebars), so there the snapshot is always "ad-free" and nothing changes.
 */
const RAIL_ID = 'wgis-blog-rail'

function subscribeToRail(onStoreChange: () => void): () => void {
  const rail = document.getElementById(RAIL_ID)
  if (!rail) return () => {}
  const observer = new MutationObserver(onStoreChange)
  observer.observe(rail, { childList: true, subtree: true })
  return () => observer.disconnect()
}

function railIsAdFree(): boolean {
  const rail = document.getElementById(RAIL_ID)
  if (!rail) return true
  return rail.querySelector('.adunit, iframe') === null
}

function useRailAdFree(): boolean {
  return useSyncExternalStore(subscribeToRail, railIsAdFree, () => true)
}

function emitCtaSurfaceShown(ctaLocation: string, props?: EventProperties) {
  const emit = analytics.ctaSurfaceShownWhenVisible ?? analytics.ctaSurfaceShown
  emit?.(ctaLocation, props)
}

// Title follows the pinned copy arm ("whole-app" since 2026-08-11, Anthony's
// call); see COPY_VARIANTS in app-download-cta.tsx for the copy map and the
// experiment history.
const SIDEBAR_TITLE = {
  control: 'Get grass ID on your phone',
  'whole-app': 'Get your lawn plan on your phone',
} as const

/**
 * Blog right-rail app card with a QR handoff. The rail is xl-only (desktop),
 * which is exactly where a "download the iPhone app" link dead-ends on Apple's
 * web preview — the QR jumps straight to the install sheet on the phone.
 * The QR + caption are further gated on input capability ((pointer: fine)) so a
 * large touch tablet that reaches the xl rail still gets the icon, headline, and
 * App Store badge but not the pointless "scan your own screen" QR.
 * Dark-glass styling matches StickySidebarCTA so the rail reads as one system.
 * Design source: Claude Design "App Store CTA Variants" 1e.
 */
export function SidebarAppCard() {
  const experimentVariant = useAppCtaCopyVariant()
  // The app is iPhone-only, so the whole card gates on the Android check, not
  // just the store badge: AppStoreBadgeLink nulls itself on Android already,
  // and the QR sits behind (pointer: fine), which would leave an Android
  // tablet a title over nothing.
  const installAvailable = useAppInstallAvailable()
  const railAdFree = useRailAdFree()
  // Denominator for the QR link's own cta_clicked. Attached to the anchor, not
  // the card, so it inherits the anchor's `(pointer: fine)` gate: a touch
  // device renders the anchor `display:none`, IntersectionObserver never
  // reports it, and the rate stays scoped to devices where the click is
  // actually possible. The badge below carries its own impression from
  // AppStoreBadgeLink, keyed app_download_blog_sidebar.
  // appInstallAvailableNow (the direct UA read) keeps the hydration render's
  // one Android frame out of the denominator — see use-app-install-available.
  const qrImpressionRef = useImpressionEvent<HTMLAnchorElement>(
    'app_download_blog_sidebar_qr',
    {
      destination: 'app_landing',
      placement: 'blog_sidebar',
      surface: 'app_sidebar_qr',
      experiment_variant: experimentVariant,
    },
    { enabled: isAppPromoLive && appInstallAvailableNow(), emit: emitCtaSurfaceShown },
  )
  if (!isAppPromoLive || !installAvailable || !railAdFree) return null

  return (
    <div
      className="flex flex-col items-center gap-3 rounded-[18px] border border-white/20 bg-[rgba(10,28,18,0.34)] p-5 text-center backdrop-blur-xl"
      style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.30)' }}
    >
      <div className="flex items-center gap-2.5">
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={36}
          height={36}
          loading="lazy"
          className="h-9 w-9 rounded-[9px]"
        />
        <span className="font-editorial text-base font-semibold leading-snug text-white">
          {SIDEBAR_TITLE[experimentVariant]}
        </span>
      </div>
      <a
        ref={qrImpressionRef}
        href="/app?from=blog_sidebar"
        onClick={() =>
          trackEvent('cta_clicked', {
            cta_location: 'app_download_blog_sidebar_qr',
            cta_text: 'QR card',
            destination: 'app_landing',
            placement: 'blog_sidebar',
            experiment_variant: experimentVariant,
          })
        }
        aria-label="Learn more about the What Grass Is This? iPhone app"
        className="hidden rounded-xl border border-white/60 bg-white p-2 transition-transform hover:scale-[1.03] [@media(pointer:fine)]:block"
      >
        <AppStoreQrCode placement="blog_sidebar" size={96} className="h-24 w-24" />
      </a>
      <p className="hidden text-xs leading-relaxed text-white/75 [@media(pointer:fine)]:block">
        Scan with your iPhone camera
      </p>
      <AppStoreBadgeLink placement="blog_sidebar" className="scale-[0.85]" />
    </div>
  )
}
